import { equals } from 'ramda';

import states from '../states';
import ducks from '../../ducks/ducks';

import { getIsUnknownDestination } from '../../selectors/carDestination';
import { getAllowHidingDestinationProcessInDigital, getClaim, getCoreRentalMission, getMissionId } from '../../selectors/coreApi';
import { getEntityNotFound, getErrorCode } from '../../selectors/error';
import {
  getIsClaimTimeline,
  getIsGarageDestinationMode,
  getIsLocationOnlyMode,
  getIsRentalCarMode,
  getSession,
  isApiSession
} from '../../selectors/global';

import {
  isCancelMissionCostFailed,
  isCoreRentalMissionOngoing,
  isEligible,
  isMissionCancelled,
  isMissionEnded,
  isMissionInProgress,
  isTOWService,
  shouldFillAppointment,
  shouldFillCarDestination,
  shouldFillEventClaim,
  shouldFillHighwayDirectionClaim,
  shouldFillHighwayMetresClaim,
  shouldFillLocationClaim,
  shouldFillLocationDetailsClaim
} from '../../utils/coreApi';
import { eligibilityWorkflow } from '../../utils/settings';
import { isNilOrEmpty } from '../../utils/helpers';
import { getActionsFromDucks } from '../utils';
import { getIsTimeLineConfirmed } from '../../webStorage';
import { CONFIRMATION_ERROR, CONFIRMATION_ERROR_CALL_ASSISTANCE, MISSION_MONITORING_ERROR, PHONE_CONTACT } from '../../ducks/coreApi';
import { ERROR_CODES } from '../../enums';

const actions = getActionsFromDucks(ducks);

export const isMissionError = (state, errorCode) => getMissionId(state) && (errorCode || getErrorCode(state));

export const isEligibleError = (state, errorCode) => isEligible(state) && (errorCode || getErrorCode(state));

export const isNotEligibleError = state => !isEligible(state);

export const isUnknownDestMission = state => getIsUnknownDestination(state) && getMissionId(state);

export const shouldCallProviderWrongData = state => eligibilityWorkflow.wrongData.isCallProvider(state);
export const shouldCallProviderNotCovered = state => eligibilityWorkflow.notCoveredOrExceeded.isCallProvider(state);

export const shouldCallProviderBadClaim = state =>
  (shouldCallProviderWrongData(state) && getEntityNotFound(state)) || (shouldCallProviderNotCovered(state) && !getEntityNotFound(state));

export const callMeBackError = (cond, errorActions) => ({
  cond,
  target: states.CALL_ME_BACK,
  actions: errorActions,
});

export const rentalExtensionClosedDayExit = errorCode => ({
  actions: [actions.coreApi.createRentalMissionDigitalExit(errorCode), actions.error.setTechnicalError(PHONE_CONTACT)],
  target: states.CALL_ME_BACK,
});

export const claimWorkflow = currentState => [
  {
    cond: state => equals(getIsTimeLineConfirmed(getSession(state)), false),
    target: states.PREVIOUS_CLAIM_HANDLING,
  },
  {
    cond: state => shouldFillEventClaim(getClaim(state)),
    target: states.EVENTS,
  },
  {
    cond: state => shouldFillLocationClaim(getClaim(state)),
    target: states.VEHICLE_LOCATION,
  },
  {
    cond: state => shouldFillLocationDetailsClaim(getClaim(state)),
    target: states.LOCATION_DETAILS,
  },
  {
    cond: shouldFillHighwayMetresClaim,
    target: states.MILESTONE,
  },
  {
    cond: shouldFillHighwayDirectionClaim,
    actions: [actions.coreApi.getHighwaySections],
    target: currentState,
  },
];

export const missionWorkflow = currentState => [
  {
    cond: state => equals(getIsTimeLineConfirmed(getSession(state)), false),
    target: states.PREVIOUS_CLAIM_HANDLING,
  },
  {
    cond: state => isApiSession(state) && getIsGarageDestinationMode(state),
    target: currentState,
    actions: [actions.coreApi.loadCarDestinationsInfo],
  },
  {
    target: currentState,
    cond: state => isTOWService(state) && !isMissionInProgress(state) && !isMissionEnded(state) && !isMissionCancelled(state),
    actions: [actions.coreApi.loadCarDestinationsInfo],
  },
  {
    cond: state => isApiSession(state) && getIsLocationOnlyMode(state),
    target: states.VEHICLE_LOCATION,
  },
  {
    cond: state =>
      (isApiSession(state) && getIsClaimTimeline(state)) || (getIsRentalCarMode(state) && !isNilOrEmpty(getCoreRentalMission(state))),
    target: states.PREVIOUS_CLAIM_HANDLING,
  },
  {
    cond: state => getIsRentalCarMode(state),
    target: states.RENTAL_CAR_WELCOME,
  },
  {
    target: states.MISSION_MONITORING,
    cond: state => isMissionInProgress(state),
    actions: [actions.coreApi.startMissionMonitoring],
  },
  {
    target: states.END_MISSION,
    cond: isMissionEnded,
  },
  {
    target: states.CALL_ME_BACK,
    cond: isMissionCancelled,
    actions: [actions.error.setTechnicalError(MISSION_MONITORING_ERROR)],
  },
  {
    target: states.APPOINTMENT,
    cond: shouldFillAppointment,
  },
  {
    target: states.CONFIRMATION,
  },
];

export const carDestinationsInfoWorkflow = [
  {
    cond: state => equals(getIsTimeLineConfirmed(getSession(state)), false),
    target: states.PREVIOUS_CLAIM_HANDLING,
  },
  {
    cond: state => isApiSession(state) && getIsClaimTimeline(state),
    target: states.PREVIOUS_CLAIM_HANDLING,
  },
  {
    cond: state => isApiSession(state) && getIsLocationOnlyMode(state),
    target: states.VEHICLE_LOCATION,
  },
  {
    cond: state => isApiSession(state) && getIsGarageDestinationMode(state),
    target: states.CAR_DESTINATIONS,
  },
  {
    cond: getAllowHidingDestinationProcessInDigital,
    target: states.APPOINTMENT,
    actions: [actions.coreApi.putVehicleDestination],
  },
  {
    cond: shouldFillCarDestination,
    target: states.CAR_DESTINATIONS,
  },
  {
    cond: shouldFillAppointment,
    target: states.APPOINTMENT,
  },
  {
    target: states.CONFIRMATION,
  },
];

export const errorWorkflow = (tagError = {}, errorCode, origin) => [
  callMeBackError(
    state =>
      isMissionError(state) && !isCancelMissionCostFailed(state) && shouldCallProviderNotCovered(state) && origin === states.CONFIRMATION,
    [actions.error.setTechnicalError(CONFIRMATION_ERROR_CALL_ASSISTANCE)]
  ),
  callMeBackError(
    state => isMissionError(state) && !isCancelMissionCostFailed(state),
    [actions.coreApi.createMissionDigitalExit(errorCode), actions.error.setTechnicalError(tagError)]
  ),
  callMeBackError(
    state =>
      isEligibleError(state, errorCode) && eligibilityWorkflow.notCoveredOrExceeded.isCallProvider(state) && origin === states.CONFIRMATION,
    [actions.error.setTechnicalError(CONFIRMATION_ERROR_CALL_ASSISTANCE)]
  ),
  callMeBackError(
    state => isEligibleError(state, errorCode),
    [actions.coreApi.createClaimDigitalExit(errorCode), actions.error.setTechnicalError(tagError)]
  ),
  {
    target: states.VOICE_EXIT,
    actions: [actions.error.setTechnicalError(tagError)],
  },
];

export const confirmationErrorWorkflow = () => [
  callMeBackError(
    state => isMissionError(state) && shouldCallProviderNotCovered(state),
    [actions.error.setTechnicalError(CONFIRMATION_ERROR_CALL_ASSISTANCE)]
  ),
  callMeBackError(
    state => isMissionError(state),
    [actions.coreApi.createMissionDigitalExit(), actions.error.setTechnicalError(CONFIRMATION_ERROR)]
  ),
  callMeBackError(
    state => isEligibleError(state) && shouldCallProviderNotCovered(state),
    [actions.error.setTechnicalError(CONFIRMATION_ERROR_CALL_ASSISTANCE)]
  ),
  callMeBackError(
    state => isEligibleError(state),
    [actions.coreApi.createClaimDigitalExit(), actions.error.setTechnicalError(CONFIRMATION_ERROR)]
  ),
  callMeBackError(
    state => isUnknownDestMission(state) && shouldCallProviderNotCovered(state),
    [actions.error.setTechnicalError(CONFIRMATION_ERROR_CALL_ASSISTANCE)]
  ),
  callMeBackError(
    state => isUnknownDestMission(state),
    [actions.coreApi.createMissionDigitalExit(ERROR_CODES.UNKNOWN_DESTINATION), actions.error.setTechnicalError()]
  ),
];

export const rentalErrorWorkflow = () => [
  {
    cond: state => isCoreRentalMissionOngoing(state),
    target: states.CALL_ME_BACK,
    actions: [actions.coreApi.createRentalMissionDigitalExit(ERROR_CODES.MISSION_ALREADY_EXISTS), actions.error.setTechnicalError()],
  },
];
